import React from "react";
import localize, { LocKeys } from "../../constants/localizations";
import ClientsIcon from "../../shared/icons/navigation/ClientsIcon";
import InvoicesIcon from "../../shared/icons/navigation/InvoicesIcon";
import JobNavIcon from "../../shared/icons/navigation/JobNavIcon";
import LodSpecificationIcon from "../../shared/icons/navigation/LodSpecificationIcon";
import MyOrganisationIcon from "../../shared/icons/navigation/MyOrganisationIcon";
import ProjectsIcon from "../../shared/icons/navigation/ProjectsIcon";
import QuotesIcon from "../../shared/icons/navigation/QuotesIcon";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import { getPermissions, tokenParams } from "../local-storage.util";
import { feature, permissions } from "../../constants/permissionConstants";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AdministrationIcon from "../../shared/icons/navigation/AdministrationIcon";
import {
	checkUserPriority,
	isBimify,
	isClient,
	isLoggedUserCustomizer,
	isLoggedUserNormalizer,
	isLoggedUserQA,
	isReseller,
	isSupplier,
	isBimifyBo,
	isLoggedUserAdmin,
	isLoggedUserBO,
	isLoggedUserGuest,
} from "../permission/user";
import { PRIORITY_USER_ROLE } from "../../constants/constants";
import TicketSidebar from "../../components/ticketing/TicketSidebar";
import SubscriptionsIcon from "../../shared/icons/navigation/SubscriptionsIcon";
import InvoicesIcon2 from "../../shared/icons/navigation/InvoicesIcon2";
import PackageIcon from "../../shared/icons/navigation/PackageIcon";
import { EuroSymbol } from "@mui/icons-material";

export const getPaths = () => {
	const isLoggedUser =
		!isLoggedUserCustomizer() && !isLoggedUserNormalizer() && !isLoggedUserQA();

	const dashboardPath = isLoggedUser ? ["/model-store", "/"] : ["/model-store"];
	const jobPath = isLoggedUser ? ["/jobs"] : ["/jobs", "/"];
	const buildingPath = ["/buildings"];

	return { buildingPath, jobPath, dashboardPath };
};
//Return menu items based on role type and company type
export const getMenu = () => {
	const loggedUser = tokenParams();
	const loggedUserPermissions = getPermissions(); //loggedUser.permissions;
	const isBimifyAdmin = loggedUser.isBimifyAdmin;
	const isPriorityUser = checkUserPriority(PRIORITY_USER_ROLE.PROJECT_LEAD);
	const items = [];
	const { buildingPath, jobPath, dashboardPath } = getPaths();

	const canViewFinance = () => {
		return (
			(isReseller() || isClient() || isSupplier()) &&
			(isLoggedUserAdmin() || isLoggedUserBO()) &&
			loggedUserPermissions?.invoices?.permissions?.includes(permissions.view)
		);
	};

	const getJobMenu = () => {
		if (isBimifyAdmin || isBimifyBo()) {
			return [
				{
					route: "/jobs",
					label: localize(LocKeys.JOBS),
					icon: <JobNavIcon className="icon" />,
				},
				{
					route: "/production-capacity",
					label: localize(LocKeys.PRODUCTION_CAPACITY),
					icon: <PackageIcon className="icon" />,
				},
			];
		}

		return [
			{
				route: "/jobs",
				label: localize(LocKeys.JOBS),
				icon: <JobNavIcon className="icon" />,
			},
		];
	};

	const getSubscriptionsMenu = () => {
		if (isBimifyAdmin || isBimifyBo()) {
			return [
				{
					route: "/subscriptions",
					label: localize(LocKeys.SUBSCRIPTIONS),
					icon: <SubscriptionsIcon className="icon" />,
				},
				{
					route: "/invoices",
					label: localize(LocKeys.INVOICES),
					icon: <InvoicesIcon2 className="icon" />,
				},
			];
		}

		if (canViewFinance()) {
			return [
				{
					route: "/subscriptions",
					label: localize(LocKeys.PLANS_AND_SUBSCRIPTIONS),
					icon: <SubscriptionsIcon className="icon" />,
				},
				{
					route: "/production-capacity",
					label: localize(LocKeys.CREDITS_AND_CONSUMPTION),
					icon: <EuroSymbol className="icon" />,
				},
			];
		}

		return [
			{
				route: "/invoices",
				label: localize(LocKeys.INVOICES),
				icon: <InvoicesIcon2 className="icon" />,
			},
		];
	};

	const getInvoiceMenuForRegularUsers = () => {
		return [
			{
				route: "/subscriptions",
				label: localize(LocKeys.PLANS_AND_SUBSCRIPTIONS),
				icon: <SubscriptionsIcon className="icon" />,
			},
			{
				route: "/credit-consumption",
				label: localize(LocKeys.CREDITS_AND_CONSUMPTION),
				icon: <EuroSymbol className="icon" />,
			},
			{
				route: "/invoices",
				label: localize(LocKeys.INVOICES),
				icon: <InvoicesIcon2 className="icon" />,
			},
		];
	};

	for (const [, value] of Object.entries(feature)) {
		switch (value) {
			case feature.dashboard:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.dashboard?.permissions?.includes(
						permissions.view
					)
				) {
					items.push({
						route: "/model-store",
						description: localize(LocKeys.DASHBOARD),
						alt: localize(LocKeys.DASHBOARD),
						image: <DashboardIcon style={{ fontSize: 32 }} />,
						activeRoutes: dashboardPath,
						wrapperClasses: isBimifyAdmin ? "mt-auto" : "",
					});
				}
				break;
			case feature.settings:
				if (
					isBimifyAdmin ||
					(isPriorityUser &&
						loggedUserPermissions?.accountSettings?.permissions?.includes(
							permissions.view
						))
				) {
					items.push({
						route: "/settings",
						description: localize(LocKeys.SETTINGS),
						alt: "Clients icon",
						image: <MyOrganisationIcon />,
					});
				}
				break;

			case feature.customers:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.customers?.permissions?.includes(
						permissions.view
					)
				) {
					items.push({
						route: "/customers",
						description: localize(LocKeys.CUSTOMERS),
						alt: "Clients icon",
						image: <ClientsIcon />,
					});
				}
				break;

			case feature.buildings:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.buildings?.permissions?.includes(
						permissions.view
					)
				) {
					items.push({
						route: "/buildings",
						description: localize(LocKeys.BUILDINGS),
						alt: "Orders icon",
						image: <ProjectsIcon />,
						activeRoutes: buildingPath,
					});
				}
				break;

			case feature.jobs:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.jobs?.permissions?.includes(permissions.view)
				) {
					items.push({
						route: "/jobs",
						// description: localize(LocKeys.JOBS),
						alt: "Jobs icon",
						image: <JobNavIcon />,
						submenu: getJobMenu(),
						tooltiptextClasses: "sidebar__submenu-tooltip",

						activeRoutes: [...jobPath, "/production-capacity"],
					});
				}
				break;

			case feature.ticketing:
				// Guests cannot interact with ticketing at all (permission matrix v2).
				if (!isLoggedUserGuest()) {
					items.push({
						route: "/support",
						description: localize(LocKeys.SUPPORT),
						alt: localize(LocKeys.SUPPORT),
						image: <TicketSidebar route="/support" />,
					});
				}

				break;

			case feature.offers:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.offers?.permissions?.includes(permissions.view)
				) {
					items.push({
						route: "/offers",
						description: localize(LocKeys.OFFERS),
						alt: "Orders icon",
						image: <QuotesIcon />,
						activeRoutes: ["/offers"],
					});
				}
				break;

			case feature.invoices:
				if (isBimifyAdmin || isBimifyBo()) {
					items.push({
						route: "/subscriptions",
						// description: localize(LocKeys.FINANCE) || "Finance",
						alt: "Finance icon",
						image: <InvoicesIcon />,
						submenu: getSubscriptionsMenu(),
						tooltiptextClasses: "sidebar__submenu-tooltip",
						activeRoutes: ["/subscriptions", "/subscriptions/new", "/invoices"],
					});
				}

				if (canViewFinance()) {
					items.push({
						route: "/subscriptions",
						alt: "Finance icon",
						image: <InvoicesIcon />,
						submenu: getInvoiceMenuForRegularUsers(),
						tooltiptextClasses: "sidebar__submenu-tooltip",
						activeRoutes: [
							"/subscriptions",
							"/subscriptions/new",
							"/production-capacity",
							"/invoices",
						],
					});
				}
				break;

			case feature.lodSpecifications:
				if (
					isBimifyAdmin ||
					loggedUserPermissions?.lodSpecifications?.permissions?.includes(
						permissions.view
					)
				) {
					items.push({
						route: "/bim-model-specification",
						description: localize(LocKeys.LOD_SPECIFICATIONS),
						alt: localize(LocKeys.LOD_SPECIFICATIONS),
						image: <LodSpecificationIcon />,
					});
				}
				break;

			case feature.docs:
				if (isPriorityUser && !!isBimify()) {
					items.push({
						route: "/administration",
						description: "Administration",
						alt: "Administration icon",
						image: <AdministrationIcon />,
						wrapperClasses: "mt-auto",
					});
				}

				if (isBimifyAdmin) {
					items.push({
						route: "/docs",
						description: "Docs",
						alt: "Code icon",
						image: <CodeOutlinedIcon />,
						wrapperClasses: "mb-regular",
					});
				}
				break;

			default:
				break;
		}
	}

	return items;
};
